import {useLocation} from '@github-ui/react-router'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useMemo} from 'react'

import {
  GROUP_BY_PARAM,
  type Layout,
  LAYOUT_PARAM,
  SWIMLANE_BY_PARAM,
  type SavedViewUrlParams,
  VISIBLE_FIELDS_PARAM,
  writeSavedViewOverrides,
} from '../../utils/saved-view-url-params'

interface SavedViewUrlActions {
  /**
   * Writes every change in one navigate. Separate navigates would each start from the same
   * `search`, so the second would overwrite the first.
   */
  applyUrlChanges: (changes: Partial<SavedViewUrlParams>) => void
  /** The URL `applyUrlChanges` navigates to for `changes`, or `null` when they change nothing. */
  urlForChanges: (changes: Partial<SavedViewUrlParams>) => string | null
  setLayout: (layout: Layout) => void
  setGroupBy: (groupBy: string | null) => void
  setSwimlaneBy: (swimlaneBy: string | null) => void
  /**
   * Removes every URL param this hook manages in a single navigate. Sequential setter
   * calls each close over the same `search` and would race — the second navigate would
   * overwrite the first. Hosts wire this up as `onReset`.
   */
  clearOverrides: () => void
}

interface UseSavedViewUrlActionsInput {
  /** Include `swimlaneBy` or `visibleFields` to manage those params in the URL. */
  baseline: SavedViewUrlParams
  current: SavedViewUrlParams
  /**
   * The URL search that changes build on. Defaults to the URL on screen. A host passes the
   * search of a navigation still loading, so a second change keeps the first.
   */
  search?: string
}

/** The params a change can rewrite in place. They pick how the view is shown, not which results it holds. */
const REPLACEABLE_PARAMS: ReadonlySet<string> = new Set([
  GROUP_BY_PARAM,
  SWIMLANE_BY_PARAM,
  VISIBLE_FIELDS_PARAM,
  'page',
])

function differsOnlyInReplaceableParams(a: URLSearchParams, b: URLSearchParams): boolean {
  const kept = (params: URLSearchParams) => {
    const copy = new URLSearchParams([...params].filter(([key]) => !REPLACEABLE_PARAMS.has(key)))
    copy.sort()
    return copy.toString()
  }
  return kept(a) === kept(b)
}

/**
 * Writes changes relative to the host-supplied baseline and current configuration.
 * Hosts own how current configuration is resolved: Planning supplies the server-applied
 * configuration, while repository saved views use their explicit legacy URL adapter.
 *
 * A change that differs from the URL on screen only in grouping, swimlane, or visible fields
 * replaces the current history entry, so Back skips the options someone tried. Anything else,
 * such as a layout change or a query still loading under `search`, adds an entry.
 */
export function useSavedViewUrlActions({
  baseline: baselineInput,
  current: currentInput,
  search: searchInput,
}: UseSavedViewUrlActionsInput): SavedViewUrlActions {
  // Not `useRenderedLocation`: `replace` acts on the current history entry, which TanStack Router moves to a pending URL before it loads.
  const {pathname, search: locationSearch} = useLocation()
  const search = searchInput ?? locationSearch
  const navigate = useNavigate()
  const {
    layout: baselineLayout,
    groupBy: baselineGroupBy,
    swimlaneBy: baselineSwimlaneBy,
    visibleFields: baselineVisibleFields,
  } = baselineInput
  const baseline: SavedViewUrlParams = useMemo(
    () => ({
      layout: baselineLayout,
      groupBy: baselineGroupBy,
      swimlaneBy: baselineSwimlaneBy,
      visibleFields: baselineVisibleFields,
    }),
    [baselineLayout, baselineGroupBy, baselineSwimlaneBy, baselineVisibleFields],
  )
  const {layout: currentLayout, groupBy: currentGroupBy, swimlaneBy: currentSwimlaneBy} = currentInput
  const currentConfiguration: SavedViewUrlParams = useMemo(
    () => ({layout: currentLayout, groupBy: currentGroupBy, swimlaneBy: currentSwimlaneBy}),
    [currentGroupBy, currentLayout, currentSwimlaneBy],
  )

  const writeChanges = useCallback(
    (changes: Partial<SavedViewUrlParams>) => {
      const current = new URLSearchParams(search)
      const next = writeSavedViewOverrides(current, changes, baseline, currentConfiguration)
      const query = next.toString()
      if (query === current.toString()) return null
      return {url: query ? `${pathname}?${query}` : pathname, params: next}
    },
    [baseline, currentConfiguration, pathname, search],
  )

  const urlForChanges = useCallback(
    (changes: Partial<SavedViewUrlParams>) => writeChanges(changes)?.url ?? null,
    [writeChanges],
  )

  const applyUrlChanges = useCallback(
    (changes: Partial<SavedViewUrlParams>) => {
      const written = writeChanges(changes)
      if (!written) return
      const replace = differsOnlyInReplaceableParams(written.params, new URLSearchParams(locationSearch))
      navigate(written.url, {preventAutofocus: true, replace})
    },
    [locationSearch, navigate, writeChanges],
  )

  const setLayout = useCallback((layout: Layout) => applyUrlChanges({layout}), [applyUrlChanges])
  const setGroupBy = useCallback((groupBy: string | null) => applyUrlChanges({groupBy}), [applyUrlChanges])
  const setSwimlaneBy = useCallback((swimlaneBy: string | null) => applyUrlChanges({swimlaneBy}), [applyUrlChanges])

  const clearOverrides = useCallback(() => {
    const current = new URLSearchParams(search)
    const next = new URLSearchParams(current)
    next.delete(LAYOUT_PARAM)
    next.delete(GROUP_BY_PARAM)
    if (baselineSwimlaneBy !== undefined) next.delete(SWIMLANE_BY_PARAM)
    if (baselineVisibleFields !== undefined) next.delete(VISIBLE_FIELDS_PARAM)
    const query = next.toString()
    if (query === current.toString()) return
    const replace = differsOnlyInReplaceableParams(next, new URLSearchParams(locationSearch))
    navigate(query ? `${pathname}?${query}` : pathname, {preventAutofocus: true, replace})
  }, [baselineSwimlaneBy, baselineVisibleFields, locationSearch, navigate, pathname, search])

  return {
    applyUrlChanges,
    urlForChanges,
    setLayout,
    setGroupBy,
    setSwimlaneBy,
    clearOverrides,
  }
}
